import { useNavigate } from 'react-router-dom'

import { SETTINGS_GROUPS, sectionTitle } from './settingsSections'

const ICON_PATHS = {
	general:
		'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.25a7.5 7.5 0 0115 0',
	security:
		'M12 3l7.5 3v5.5c0 4.6-3.1 8.7-7.5 10.2-4.4-1.5-7.5-5.6-7.5-10.2V6z',
	devices: 'M3.75 5.25h16.5v10.5H3.75zM8.25 20.25h7.5',
	subscription: 'M3 6.75h18v10.5H3zM3 10.5h18',
	team:
		'M12.75 9a2.75 2.75 0 11-5.5 0 2.75 2.75 0 015.5 0zM3.75 19.5c0-3 2.6-4.9 6.25-4.9s6.25 1.9 6.25 4.9M16 7.25a2.75 2.75 0 010 5.2M18.25 14.9c2 .7 3 2.2 3 4.6',
	telegram: 'M21 4.5L3 11.25l6 2.25L19.5 7l-7.8 8.4.5 4.8 2.8-3.6',
	email: 'M3 6.75h18v10.5H3zM3.3 7.2l8.7 5.4 8.7-5.4',
}

function SectionIcon({ tab, isDarkMode }) {
	return (
		<svg
			className={`h-5 w-5 flex-none ${
				isDarkMode ? 'text-gray-400' : 'text-gray-500'
			}`}
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth="1.7"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d={ICON_PATHS[tab]} />
		</svg>
	)
}

function Chevron({ isDarkMode }) {
	return (
		<svg
			className={`ml-auto h-4 w-4 flex-none ${
				isDarkMode ? 'text-gray-500' : 'text-gray-400'
			}`}
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M9 5l7 7-7 7" />
		</svg>
	)
}

function SettingsList({ isDarkMode, onOpen }) {
	return (
		<div className="overflow-x-hidden px-4 pb-10 pt-2">
			<h1
				className={`mb-6 text-2xl font-semibold ${
					isDarkMode ? 'text-white' : 'text-gray-900'
				}`}
			>
				Settings
			</h1>

			{SETTINGS_GROUPS.map(group => (
				<div key={group.label} className="mb-6">
					<p
						className={`mb-2 px-1 text-xs font-semibold uppercase tracking-wider ${
							isDarkMode ? 'text-gray-400' : 'text-gray-500'
						}`}
					>
						{group.label}
					</p>
					<div
						className={`overflow-hidden rounded-xl border ${
							isDarkMode
								? 'border-white/10 bg-gray-800/50'
								: 'border-gray-200 bg-white'
						}`}
					>
						{group.items.map((item, index) => (
							<a
								key={item.tab}
								href={item.path}
								onClick={event => {
									event.preventDefault()
									onOpen(item.path)
								}}
								className={`flex min-h-[52px] items-center gap-3 px-4 py-3 text-base no-underline ${
									isDarkMode
										? 'text-gray-100 active:bg-gray-700'
										: 'text-gray-900 active:bg-gray-100'
								} ${
									index > 0
										? isDarkMode
											? 'border-t border-white/10'
											: 'border-t border-gray-200'
										: ''
								}`}
							>
								<SectionIcon tab={item.tab} isDarkMode={isDarkMode} />
								{item.label}
								<Chevron isDarkMode={isDarkMode} />
							</a>
						))}
					</div>
				</div>
			))}
		</div>
	)
}

function SettingsDetail({ activeTab, isDarkMode, onBack, children }) {
	return (
		<div className="flex h-full flex-col">
			<div
				className={`sticky top-0 z-10 flex items-center gap-1 border-b px-2 py-2 ${
					isDarkMode
						? 'border-white/10 bg-dark-color'
						: 'border-gray-200 bg-white'
				}`}
			>
				<button
					type="button"
					onClick={onBack}
					aria-label="Back to settings"
					className={`flex min-h-[44px] items-center gap-1 rounded-md px-2 text-base ${
						isDarkMode ? 'text-blue-400' : 'text-blue-600'
					}`}
				>
					<svg
						className="h-5 w-5"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<path d="M15 5l-7 7 7 7" />
					</svg>
					{/* Under ~360px the word collides with a long centred title. */}
					<span className="hidden min-[360px]:inline">Settings</span>
				</button>
				<span
					className={`pointer-events-none absolute inset-x-0 truncate px-24 text-center text-base font-semibold ${
						isDarkMode ? 'text-white' : 'text-gray-900'
					}`}
				>
					{sectionTitle(activeTab)}
				</span>
			</div>

			{/* A section that is wider than the phone scrolls inside its own box;
			    it must never widen the page itself. */}
			<div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 pb-10 pt-4">
				{children}
			</div>
		</div>
	)
}

/**
 * Settings below the tablet breakpoint: a list of sections that opens one
 * section at a time, instead of the desktop rail and pane. The sections
 * themselves are the same components the desktop renders.
 */
export default function SettingsMobile({
	activeTab,
	showList,
	isDarkMode,
	children,
}) {
	const navigate = useNavigate()

	if (showList) {
		return (
			<SettingsList
				isDarkMode={isDarkMode}
				onOpen={path => navigate(path)}
			/>
		)
	}

	return (
		<SettingsDetail
			activeTab={activeTab}
			isDarkMode={isDarkMode}
			onBack={() => navigate('/settings')}
		>
			{children}
		</SettingsDetail>
	)
}
